/**
 * Cross-tab sync for the in-app notification inbox.
 * Mercure SSE is per-tab and often stalls when many Yoda tabs are open;
 * BroadcastChannel mirrors inbox events so badge/list stay aligned.
 */

export const NOTIFICATION_INBOX_CHANNEL = 'yoda-notification-inbox';

export type NotificationInboxUpdate = {
    type: string;
    data: AnyObj;
};

export type NotificationInboxMessage = {
    originTabId: string;
    update: NotificationInboxUpdate;
};

let tabId: string | null = null;

export function getNotificationInboxTabId(): string {
    if (!tabId)
        tabId = typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return tabId;
}

export function isNotificationInboxEvent(type: unknown): type is string {
    return typeof type === 'string' && /^notification\./.test(type);
}

/** Avoid double-counting when Mercure and BroadcastChannel deliver the same create. */
export function nextUnreadCountAfterCreate(
    unreadCount: number,
    previous: { readAt?: unknown } | undefined,
    notification: { readAt?: unknown }
): number {
    const alreadyUnread = Boolean(previous && !previous.readAt);
    const nextUnread = !notification.readAt;
    if (!alreadyUnread && nextUnread)
        return unreadCount + 1;
    return unreadCount;
}

export function createNotificationInboxChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === 'undefined')
        return null;
    return new BroadcastChannel(NOTIFICATION_INBOX_CHANNEL);
}

/** Plain JSON for BroadcastChannel (Moment / class instances do not clone cleanly). */
export function serializeNotificationInboxData(data: AnyObj): AnyObj {
    if (!data || typeof data !== 'object')
        return data;
    const serialized: AnyObj = { ...data };
    for (const key of ['createdAt', 'updatedAt', 'readAt']) {
        const value = serialized[key];
        if (value && typeof value === 'object' && typeof value.toISOString === 'function')
            serialized[key] = value.toISOString();
    }
    return serialized;
}

export function publishNotificationInboxUpdate(
    channel: BroadcastChannel | null,
    update: NotificationInboxUpdate
): void {
    if (!channel || !isNotificationInboxEvent(update?.type))
        return;
    const message: NotificationInboxMessage = {
        originTabId: getNotificationInboxTabId(),
        update: {
            type: update.type,
            data: serializeNotificationInboxData(update.data || {}),
        },
    };
    channel.postMessage(message);
}

export function parseNotificationInboxMessage(raw: unknown): NotificationInboxMessage | null {
    if (!raw || typeof raw !== 'object')
        return null;
    const message = raw as NotificationInboxMessage;
    if (!message.originTabId || !message.update || !isNotificationInboxEvent(message.update.type))
        return null;
    if (message.originTabId === getNotificationInboxTabId())
        return null;
    return message;
}
